// Make the entire project compatible with everything
import "core-js/stable";
import "regenerator-runtime/runtime";

import Vue from "vue";
import { sync } from "vuex-router-sync";
import * as Sentry from "@sentry/browser";
import * as Integrations from "@sentry/integrations";
import App from "@/components/App";
import { createRouter } from "@/router";
import { createStore } from "@/store";
import BootstrapVue from "bootstrap-vue";
//import VueI18n from 'vue-i18n'
import axios from "axios";
import VueJwtDecode from "vue-jwt-decode";
import VueGtm from "vue-gtm";
import _ from "lodash";

// original styles from bootstrap
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap-vue/dist/bootstrap-vue.css";

// load bootstrap overriding styles for this project
import "@/assets/css/themes/default/bootstrap-vue-theme.css";

// Keep in main RTL bootstrap styles:
// https://github.com/DediData/Bootstrap-RTL
// https://github.com/morteza/bootstrap-rtl

// load non bootstrap styles
import "@/assets/css/aspect-ratio.css";
import "@/assets/scss/themes/default/main.scss";

// api rest lib generated from swagger.json
import { setDomain } from "@/lib/api-client.js";
// custom lib to configure api requests
import { getApiUri } from "@/lib/api-config.js";

// extra custom libs and plugins
import VueBootstrapUtilForm from "@/plugins/vue-bootstrap-utilform";
import PdvNotifications from "@/plugins/pdv-notifications.js";
import i18n from "@/lib/i18n";
import DevelatioVueSentry from "@/plugins/vue-sentry.js";
import VueI18nFallback from "@/plugins/global-fallback-i18n";
import VueNumberFormatter from "@/plugins/global-number-formatter";
import TradukojTranslatable from "@/plugins/tradukoj-translatable";
import AuthState from "@/plugins/auth-state";
import StripHtml from "@/plugins/vue-striphtml";
import SocialSharing from "@/plugins/social-sharing";
import VueI18nFailSafe from "@/plugins/vue-i18n-fail-safe";

import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { faUserMd } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import { faPencilRuler } from "@fortawesome/free-solid-svg-icons";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { faCaretRight } from "@fortawesome/free-solid-svg-icons";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { faSearchPlus } from "@fortawesome/free-solid-svg-icons";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { faThumbsUp } from "@fortawesome/free-solid-svg-icons";
import { faFolder } from "@fortawesome/free-solid-svg-icons";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { faRedo } from "@fortawesome/free-solid-svg-icons";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";

import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { faTwitter } from "@fortawesome/free-brands-svg-icons";
import { faFacebook } from "@fortawesome/free-brands-svg-icons";
import { faGooglePlusG } from "@fortawesome/free-brands-svg-icons";
import { faPinterest } from "@fortawesome/free-brands-svg-icons";
import { faTimesCircle } from "@fortawesome/free-regular-svg-icons";

Object.defineProperty(Vue.prototype, "$_", { value: _ });

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [
      new Integrations.Vue({
        Vue,
        logErrors: true,
      }),
      new Integrations.ExtraErrorData({
        depth: 4,
      }),
    ],
    attachStacktrace: true,
    maxValueLength: 1000,
    ignoreErrors: [
      "canvas.contentDocument",
      // Facebook borked
      "fb_xd_fragment",
      // Hubspot
      "ReferenceError: hbspt is not defined",
      // https://gist.github.com/pioug/b006c983538538066ea871d299d8e8bc
      /^No error$/,
      /__show__deepen/,
      /Access is denied/,
      /anonymous function: captureException/,
      /Blocked a frame with origin/,
      /console is not defined/,
      /cordova/,
      /DataCloneError/,
      /Error: AccessDeny/,
      /event is not defined/,
      /feedConf/,
      /ibFindAllVideos/,
      /myGloFrameList/,
      /SecurityError/,
      /MyIPhoneApp/,
      /snapchat.com/,
      /vid_mate_check is not defined/,
      /win\.document\.body/,
      /window\._sharedData\.entry_data/,
      /ztePageScrollModule/,
    ],
    blacklistUrls: [
      // Google statics
      /gstatic\.com/i,
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
    ],
  });
}

// No queremos service workers (de momento)
if (process.browser) {
  if (
    navigator &&
    navigator.serviceWorker &&
    navigator.serviceWorker.getRegistrations &&
    typeof navigator.serviceWorker.getRegistrations === "function"
  ) {
    navigator.serviceWorker
      .getRegistrations()
      .then(rs => rs.map(r => r.unregister()))
      .catch(() => {
        //noop, we failed to remove the SW for some reason
      });
  }
}

// Patch for Samsung Internet (Browser) V 7.2
// see: https://github.com/SamsungInternet/support/issues/56
if (process.browser) {
  if (navigator.userAgent.match(/SamsungBrowser\/7\.2/i)) {
    Function.prototype.ToString = function() {
      return this.toString();
    };
  }
}

//  errors handling
import { handleAxiosErrors, handleAppErrors } from "@/lib/errors.js";

library.add(faUserMd);
library.add(faFilter);
library.add(faTimesCircle);
library.add(faHome);
library.add(faUser);
library.add(faUserPlus);
library.add(faEye);
library.add(faEyeSlash);
library.add(faPencilAlt);
library.add(faPencilRuler);
library.add(faPlusCircle);
library.add(faExclamationTriangle);
library.add(faCheckCircle);
library.add(faTrashAlt);
library.add(faPhone);
library.add(faEnvelope);
library.add(faInfoCircle);
library.add(faShoppingCart);
library.add(faAngleRight);
library.add(faCaretRight);
library.add(faSearch);
library.add(faSearchPlus);
library.add(faCalendarAlt);
library.add(faLock);
library.add(faThumbsUp);
library.add(faFolder);
library.add(faWhatsapp);
library.add(faTwitter);
library.add(faFacebook);
library.add(faGooglePlusG);
library.add(faPinterest);
library.add(faChevronDown);
library.add(faChevronLeft);
library.add(faChevronRight);
library.add(faRedo);
library.add(faDownload);
library.add(faCaretDown);

Vue.component("font-awesome-icon", FontAwesomeIcon);

Vue.config.productionTip = false;

// set configured url for api
setDomain(getApiUri());

// Load  plugins and modules into Vue
Vue.prototype.$http = axios;
//Vue.use(VueI18n)
Vue.use(BootstrapVue);
Vue.use(VueJwtDecode);
Vue.use(VueBootstrapUtilForm);
Vue.use(PdvNotifications);
Vue.use(VueI18nFallback);
Vue.use(VueNumberFormatter);
Vue.use(DevelatioVueSentry);
Vue.use(TradukojTranslatable);
Vue.use(AuthState);
Vue.use(StripHtml);
Vue.use(SocialSharing);
Vue.use(VueI18nFailSafe);

// ReCaptcha v3
if (process.browser) {
  const { VueReCaptcha } = require("vue-recaptcha-v3");
  Vue.use(VueReCaptcha, {
    siteKey: process.env.VUE_APP_GOOGLE_SITE_KEY,
  });
}

// Vue Awesome Swiper
if (process.browser) {
  const VueAwesomeSwiper = require("vue-awesome-swiper");
  require("swiper/dist/css/swiper.css");
  Vue.use(VueAwesomeSwiper);
}

// Date Picker
if (process.browser) {
  const datePicker = require("vue-bootstrap-datetimepicker");
  require("pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css");
  Vue.use(datePicker);
}

// Notifications
if (process.browser) {
  const Notifications = require("vue-notification").default;
  Vue.use(Notifications);
}

// Loading
if (process.browser) {
  const VueElementLoading = require("vue-element-loading");
  Vue.component("VueElementLoading", VueElementLoading);
}

// Flags and icons
if (process.browser) {
  const FlagIcon = require("vue-flag-icon").default;
  require("@/assets/scss/font-awesome.scss");
  Vue.use(FlagIcon);
}

// DOMPurify (for sanitizing html in v-html)
import VueDOMPurifyHTML from "vue-dompurify-html";
Vue.use(VueDOMPurifyHTML);

import { mapState } from "vuex";
import { mapGetters } from "vuex";

const STATUS_LOADING = 1;
const STATUS_DONE = 2;
const STATUS_DONE_WITH_ERROR = 3;

handleAxiosErrors();
handleAppErrors();

// Set properties in app
Vue.prototype.$phoneHelp =
  "5218124695404" +
  "&text=" +
  encodeURI("¡Hola Vooy Market! Solicito ayuda con ");

Vue.prototype.$phoneCustomerService = "800 083 7460";

// Store created here available in SSR mode
export var store;

// init app and export
export function createApp() {
  // create router and store instances
  const router = createRouter();
  store = createStore();

  // sync so that route state is available as part of the store
  sync(store, router);

  // Google Tag Manager
  if (process.env.NODE_ENV === "production" && process.browser) {
    Vue.use(VueGtm, {
      id: process.env.VUE_APP_GOOGLE_TAG_MANAGER_KEY,
      vueRouter: router,
    });
  }

  const app = new Vue({
    computed: {
      ...mapState({
        authenticated: state => state.auth.authenticated,
        jwt: state => state.auth.jwt,
        loading: state => state.appstatus.loading,
        categories_ng_loading_status: state =>
          state.drugs.categories_ng_loading_status,
      }),
      ...mapGetters("i18nmap", ["availableLangs"]),
      ...mapGetters("drugs", ["categoriesTree", "groupedCategories"]),
      ...mapGetters("rates", ["areRatesLoaded, isRatesError"]),
      ...mapGetters("auth", ["deviceCountry"]),
      is_all_loaded() {
        return (
          this.categoriesTree.length > 0 &&
          this.groupedCategories.length > 0 &&
          this.availableLangs &&
          [3, 2].includes(this.categories_ng_loading_status) &&
          this.deviceCountry &&
          (this.isRatesError || this.areRatesLoaded)
        );
      },
    },
    watch: {
      is_all_loaded: function(loaded) {
        if (loaded) {
          if (this.isRatesError || this.isI18nError) {
            this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
              status: STATUS_DONE_WITH_ERROR,
            });
          } else if (!this.areRatesLoaded && this.isRatesError) {
            this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
              status: STATUS_DONE_WITH_ERROR,
            });
          } else {
            this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
              status: STATUS_DONE,
            });
          }
        }
      },
    },
    mounted: function() {
      this.initApp();
    },
    created() {
      if (this.$route.query.country) {
        this.$store.commit(
          "i18nmap/MUTATE_FORCE_COUNTRY",
          this.$route.query.country
        );
      }
    },
    methods: {
      _checkAuthToken: function() {
        store.dispatch("auth/CHECK_TOKEN").then(undefined, () => {
          // Si estamos en una vista protegida redirigimos a login
          if ((this.$route.meta || {}).auth) {
            this.$router.push({
              name: "login",
              params: { backto: this.$route },
            });
          }
        });
      },
      scheduledActions: function() {
        this._checkAuthToken();
      },
      initDeviceCountry: function() {
        return this.$store
          .dispatch("auth/REQUEST_COUNTRY")
          .then(undefined, err => {
            this.$sentry({ capture: err });
            this.$router.push({ name: "fallback-error-page" });
          });
      },
      initLanguages: function() {
        // Este dispatch no devuelve un promise, podemos user try/catch
        try {
          this.$store.dispatch("i18nmap/START_I18N", {
            i18n: i18n,
            private: this.authenticated,
          });
        } catch (e) {
          this.$sentry({ capture: e });
          this.$router.push({ name: "fallback-error-page" });
        }
      },
      initCategories: function() {
        this.$store
          .dispatch("drugs/REQUEST_PHDRUGS_CATEGORIES_NG", {})
          .then(undefined, err => {
            this.$sentry({ capture: err });
            this.$router.push({ name: "fallback-error-page" });
          });
        this.$store
          .dispatch("drugs/REQUEST_PHDRUGS_GROUPED_CATEGORIES_LIST", {})
          .then(undefined, err => {
            this.$sentry({ capture: err });
            this.$router.push({ name: "fallback-error-page" });
          });
      },
      initRates: function() {
        return this.$store.dispatch("rates/FETCH_RATES").then(undefined, e => {
          this.$sentry({ capture: e });
          this.$router.push({ name: "fallback-error-page" });
        });
      },
      depurateProducts: function() {
        return this.$store.commit("shoppingcart/DEPURATE_PRODUCTS");
      },
      initShoppingCart: function() {
        return this.$store
          .dispatch("shoppingcart/REQUEST_ACTIVE_CART_PRODUCTS")
          .then(undefined, err => {
            this.$sentry({ capture: err });
            this.$router.push({ name: "fallback-error-page" });
          });
      },
      initMainPatient: function() {
        return this.$store.dispatch("naturalperson/GET_MAIN_PATIENT");
      },
      checkToken: function() {
        var promise = this.$store.dispatch("auth/CHECK_TOKEN");
        // esperamos la validación
        return promise;
      },
      initApp: function() {
        this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
          status: STATUS_LOADING,
        });

        this.initLanguages();
        let promises = [];
        const promiseCountry = this.initDeviceCountry();
        promises.push(promiseCountry);
        if (!this.areRatesLoaded && !this.isRatesError) {
          const promiseRates = this.initRates();
          promises.push(promiseRates);
        }

        this.checkToken().then(
          () => {
            // cuando está logueado cargamos las cosas necesarias
            const promiseMainPatient = this.initMainPatient();
            promises.push(promiseMainPatient);
            const pormiseShoppingCart = this.initShoppingCart();
            promises.push(pormiseShoppingCart);

            Promise.all(promises).then(
              () => {
                this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
                  status: STATUS_DONE,
                });
              },
              () => {
                this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
                  status: STATUS_DONE_WITH_ERROR,
                });
              }
            );
          },
          () => {
            // cuando no está logueado
            this.depurateProducts();
            Promise.all(promises).then(
              () => {
                this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
                  status: STATUS_DONE,
                });
              },
              () => {
                this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
                  status: STATUS_DONE_WITH_ERROR,
                });
              }
            );
          },
          err => {
            // si se produce algún tipo de error
            this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
              status: STATUS_DONE_WITH_ERROR,
            });
            this.$sentry({ capture: err });
            this.$router.push({ name: "fallback-error-page" });
          }
        );

        // Como no podemos esperar a que carguen las categorías para cargar la página
        // las cargamos independientemente
        this.initCategories();

        this.scheduledActions();
        setInterval(
          function() {
            this.scheduledActions();
          }.bind(this),
          60000
        );
      },
    },
    serverPrefetch() {
      this.$store.commit("auth/CHECK_TOKEN_BUSY", false);
      this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
        status: STATUS_LOADING,
      });
      return this.initRates().then(
        () => {
          this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
            status: STATUS_DONE,
          });
        },
        () => {
          this.$store.dispatch("appstatus/PUT_LOADING_STATUS", {
            status: STATUS_DONE_WITH_ERROR,
          });
        }
      );
    },
    i18n,
    router,
    store,
    ...App,
    render: h => h(App),
  });
  return { app, router, store };
}
